import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Panel, PanelBody, PanelHeader } from "../../components/panel/panel.jsx";
import ProductMediaGallery from "../../components/CustomComponents/ProductMediaGallery/ProductMediaGallery";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import useThemeColor from "../../hooks/useThemeColor";
import useDarkMode from "../../hooks/useDarkMode";

const ManageMedia = () => {
  const compID = useSelector((state) => state.auth.user)?.userData?.compID;
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const [seeding, setSeeding] = useState(false);
  const [galleryKey, setGalleryKey] = useState(0);

  const handleSeedFromProducts = useCallback(async () => {
    if (!compID) return;
    try {
      setSeeding(true);
      const response = await axiosInstance.post(APIs.POST_SEED_COMPANY_MEDIA, null, {
        params: { CompID: compID },
      });
      if (response.data?.status !== true) {
        throw new Error(response.data?.message || "Failed to import images");
      }
      const rows =
        response.data?.data?.rowsInserted ?? response.data?.message ?? "Images imported";
      showSuccessToast(typeof rows === "number" ? `Imported ${rows} image(s) from menu items` : String(rows));
      setGalleryKey((k) => k + 1);
    } catch (err) {
      showErrorToast(err.message || "Failed to import images from menu items");
    } finally {
      setSeeding(false);
    }
  }, [compID]);

  return (
    <div>
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item">
          <a href="#/">Home</a>
        </li>
        <li className="breadcrumb-item active">Media</li>
      </ol>
      <h1 className="page-header">
        Media Gallery <small>Manage menu images for your company</small>
      </h1>

      <div className="card border-0">
        <Panel className="m-3">
          <PanelHeader className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <span>Menu media</span>
            <button
              type="button"
              className="btn btn-sm rounded-pill"
              disabled={!compID || seeding}
              onClick={handleSeedFromProducts}
              style={{
                backgroundColor: isDarkMode ? "#3c4043" : "#fff",
                borderColor: themeColor,
                color: themeColor,
              }}
            >
              {seeding ? (
                <>
                  <i className="fa fa-spinner fa-spin me-1" />
                  Importing...
                </>
              ) : (
                <>
                  <i className="fa fa-sync me-1" />
                  Import from menu items
                </>
              )}
            </button>
          </PanelHeader>
          <PanelBody>
            <div className="bg-white p-4 rounded-3 shadow-sm">
              <p className="text-muted mb-4">
                Upload multiple product images at once or browse your company gallery. Images uploaded
                here can be reused when editing menu items.
              </p>
              {compID ? (
                <ProductMediaGallery
                  key={galleryKey}
                  compID={compID}
                  mediaCategory="Product"
                  variant="page"
                  multipleUpload
                  enableSelection={false}
                  autoSeedOnEmpty
                />
              ) : (
                <p className="text-muted text-center py-5">Company context is required to manage media.</p>
              )}
            </div>
          </PanelBody>
        </Panel>
      </div>
    </div>
  );
};

export default ManageMedia;
